/**
 * Shared network / fetch timeout detection and user-facing messages.
 */

export const FETCH_TIMEOUT_MS_DEFAULT = 60_000;
export const FETCH_TIMEOUT_MS_UPLOAD = 120_000;

const TIMEOUT_STATUS_CODES: number[] = [504, 408];
const TIMEOUT_ERROR_NAMES: string[] = ["TimeoutError"];
const TIMEOUT_MESSAGE_FRAGMENTS: string[] = [
	"timeout",
	"504",
	"408",
	"Gateway Timeout",
	"Request Timeout",
	"délai",
	"timed out",
];

/** True when `fetch` was cancelled by AbortController (navigation / new search), not a timeout. */
export function isUserAbortError(error: unknown): boolean {
	if (!(error instanceof Error) || error.name === "TimeoutError") {
		return false;
	}
	if (error.name === "AbortError") {
		return true;
	}
	const message = error.message.toLowerCase();
	if (message === "cancelled" || message === "abort") {
		return true;
	}
	return (
		message.includes("the user aborted a request") ||
		message.includes("the operation was aborted") ||
		message.includes("fetch is aborted")
	);
}

/** Safari/WebKit often rejects an aborted `fetch` as TypeError ("Load failed" / "cancelled"), not AbortError. */
export function toUserAbortError(error: unknown): Error {
	if (isUserAbortError(error) && error instanceof Error) {
		return error;
	}
	const abort = new Error(error instanceof Error ? error.message : "aborted");
	abort.name = "AbortError";
	return abort;
}

/** Timeout stays TimeoutError; any other abort on the merged signal becomes AbortError (no toast). */
export function rethrowNormalizedFetchFailure(signal: AbortSignal, error: unknown): never {
	const reason: unknown = signal.reason;
	if (reason instanceof Error && reason.name === "TimeoutError") {
		throw reason;
	}
	if (signal.aborted) {
		throw toUserAbortError(error);
	}
	throw error;
}

export function isTimeoutError(error: unknown): boolean {
	if (error === null || typeof error !== "object") {
		return false;
	}
	if (isUserAbortError(error)) {
		return false;
	}
	const errorObj = error as { status?: number; message?: string; name?: string };
	if (typeof errorObj.status === "number" && TIMEOUT_STATUS_CODES.includes(errorObj.status)) {
		return true;
	}
	if (typeof errorObj.name === "string" && TIMEOUT_ERROR_NAMES.includes(errorObj.name)) {
		return true;
	}
	const message = errorObj.message;
	return typeof message === "string" && TIMEOUT_MESSAGE_FRAGMENTS.some((fragment) => message.includes(fragment));
}

export function isNetworkTransportError(error: unknown): boolean {
	if (isTimeoutError(error)) {
		return true;
	}
	const message = error instanceof Error ? error.message : String(error);
	return (
		message.includes("Failed to fetch") ||
		message.includes("ERR_CONNECTION_RESET") ||
		message.includes("connection reset") ||
		message.includes("NetworkError") ||
		message.includes("network")
	);
}

export const FETCH_TIMEOUT_USER_MESSAGE =
	"La requête a pris trop de temps. Vérifiez votre connexion et réessayez.";

export function buildFetchTimeoutError(method: string, url: string): Error {
	const err = new Error(`${FETCH_TIMEOUT_USER_MESSAGE} (${method} ${url})`);
	err.name = "TimeoutError";
	return err;
}
